import React from 'react'
import { cn, Checkbox, ProductBadge } from '@yanolja-rnd/nol-design-system'
import { IcHelp } from '@yanolja-rnd/nol-icons'
import { DomesticCartTrainOption } from '~/api/converters/domestic/cart/ProductSection/Train/index.type'
import ProductThumbnail from '~/app/components-nds/common/thumbnail'
import CancellationPolicy from '~/app/components-nds/common/cancellation-policy'
import ProductOutdated from '~/app/components-nds/common/product-outdated'
import useModalHashV2 from '~/app/components-nds/hooks/use-modal-hash'
import { getDisabledClasses } from '../utils'
import dynamic from 'next/dynamic'
import useDomesticCartStore from '~/app/store/domestic/cart/cart'
import OptionDeleteButton from '~/app/components-nds/domestic/cart/cart-product-section/items/common/option-delete-button'
import { useDomesticV1CartQuery } from '~/app/react-query/domestic/cart/v1'

const InfoListBottomSheet = dynamic(
  () => import('~/app/components-nds/common/info-list-bottom-sheet').then(module => module.default),
  { ssr: false }
)

interface TrainOptionProps {
  /** 기차 옵션 데이터 */
  option: DomesticCartTrainOption
  /** 옵션 타이틀 클릭 시 핸들러 */
  onTitleClick: () => void
}

interface TrainDetailInfoRowProps {
  /** 라벨 텍스트 */
  label: string
  /** 표시할 내용 */
  infoTexts: string[]
  /** 불가 여부 */
  disabled: boolean
}

/**
 * 기차 옵션 정보 행 컴포넌트
 */
const TrainDetailInfoRow: React.FC<TrainDetailInfoRowProps> = ({ label, infoTexts, disabled }) => {
  return (
    <>
      <div className="flex h-[20px] min-h-20 items-center rounded-6 bg-static-white px-6 py-2">
        <span className="whitespace-nowrap typography-caption-11-bold">{label}</span>
      </div>
      <div
        className={getDisabledClasses(disabled, 'grid gap-4 typography-body-14-regular-multiline')}
      >
        {infoTexts?.map((info, index) => (
          <div key={index}>{info}</div>
        ))}
      </div>
    </>
  )
}

/**
 * 기차 옵션 컴포넌트
 */
const TrainOption: React.FC<TrainOptionProps> = ({ option, onTitleClick }) => {
  const {
    optionId,
    optionTitle,
    departureDate,
    imageUrl,
    isPackageDiscountProduct,
    priceText,
    trainGradeSeatInfo,
    priceQuantityInfo,
    cancellationPolicy,
    isSaleAvailable,
    disabledText,
    needUpdate,
    isSelectable,
  } = option

  // 장바구니 상태 관리
  const { isSelected, selectOption, unselectOption } = useDomesticCartStore(
    ({ selectedOptionIds, selectOption, unselectOption }) => {
      return {
        isSelected: selectedOptionIds.has(optionId),
        selectOption,
        unselectOption,
      }
    }
  )

  // 묶음할인상품 모달
  const [isPackageDiscountModalOpen, setIsPackageDiscountModalOpen] = useModalHashV2({
    id: 'PackageDiscountProduct',
  })
  const { refetch } = useDomesticV1CartQuery()

  // 체크박스 변경 핸들러
  const handleCheckboxChange = () => {
    if (isSelected) {
      unselectOption(optionId)
    } else {
      selectOption(optionId)
    }
  }

  return (
    <div className="flex flex-col gap-16 bg-static-white">
      {/* 헤더 영역 - 체크박스와 삭제 버튼 */}
      {isSelectable ? (
        <div className="flex items-start gap-8">
          <div className="flex w-full items-start gap-6">
            <Checkbox
              size="small"
              checked={isSelected}
              disabled={!isSaleAvailable}
              onChange={handleCheckboxChange}
            />
            <div
              className={getDisabledClasses(
                !isSaleAvailable,
                'cursor-pointer typography-subtitle-16-bold'
              )}
              tabIndex={0}
              role="button"
              aria-label="기차 검색 페이지로 이동"
              onClick={onTitleClick}
            >
              {optionTitle}
            </div>
          </div>
          <OptionDeleteButton optionId={optionId} aria-label={`${optionTitle} 삭제`} />
        </div>
      ) : (
        <div
          className={getDisabledClasses(
            !isSaleAvailable,
            'cursor-pointer typography-subtitle-16-bold'
          )}
          tabIndex={0}
          role="button"
          aria-label="기차 검색 페이지로 이동"
          onClick={onTitleClick}
        >
          {optionTitle}
        </div>
      )}

      {/* 메인 정보 영역 - 썸네일과 기본 정보 */}
      <div className="flex items-start gap-12">
        <ProductThumbnail
          image={imageUrl}
          isDisabled={!isSaleAvailable}
          className={'h-118 min-w-88 pf-pc:h-144 pf-pc:min-w-108'}
          imgClassName="!object-contain"
          alt={`${optionTitle} 기차 이미지`}
        />
        <div className={cn('flex min-h-118 w-full flex-col justify-between', 'pf-pc:min-h-144')}>
          <div className="grid gap-6">
            {/* 출발일 정보 */}
            <div
              className={getDisabledClasses(
                !isSaleAvailable,
                'break-all typography-body-14-regular-multiline'
              )}
            >
              {departureDate}
            </div>
            {isPackageDiscountProduct && (
              <button
                type="button"
                onClick={() => setIsPackageDiscountModalOpen(true)}
                className="w-fit"
              >
                <ProductBadge type="outlined_primary" size="medium">
                  묶음할인상품
                  <IcHelp width={12} height={12} />
                </ProductBadge>
              </button>
            )}
          </div>

          {/* 가격 정보 */}
          <div className="mt-12 flex flex-col items-end gap-4">
            <div
              className={getDisabledClasses(
                !isSaleAvailable,
                'break-all text-right typography-subtitle-16-bold',
                'line-through'
              )}
            >
              {priceText}
            </div>
            {!isSaleAvailable && <div className="typography-body-12-bold">{disabledText}</div>}
          </div>
        </div>
      </div>

      {/* 상세 예약 정보 */}
      <div className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-8 rounded-12 bg-fill-neutral-weak p-16">
        {/* 기차/등급/좌석 정보 */}
        <TrainDetailInfoRow
          label="선택"
          infoTexts={[trainGradeSeatInfo]}
          disabled={!isSaleAvailable}
        />

        {/* 인원 정보 */}
        <TrainDetailInfoRow
          label="인원"
          infoTexts={priceQuantityInfo}
          disabled={!isSaleAvailable}
        />
      </div>

      {/* 취소 규정 영역 */}
      {!!cancellationPolicy &&
        (cancellationPolicy.type === 'policy-error' ? (
          <CancellationPolicy {...cancellationPolicy} onRefresh={() => refetch()} />
        ) : (
          <CancellationPolicy {...cancellationPolicy} />
        ))}

      {/* 상품 정보 변경 영역 */}
      {needUpdate && <ProductOutdated />}

      {/* 묶음할인상품 모달 */}
      <InfoListBottomSheet
        isOpen={isPackageDiscountModalOpen}
        onClose={() => setIsPackageDiscountModalOpen(false)}
        header="묶음할인상품"
        contents={[
          {
            subContents: [{ content: '기차는 국내숙소와 반드시 함께 예약해야 돼요.' }],
          },
          {
            subContents: [{ content: '함께 예약하면 묶음할인을 받을 수 있어요.' }],
          },
        ]}
      />
    </div>
  )
}

export default TrainOption
